import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  AccountTree as LogicIcon,
  Code as CodeIcon,
  Schedule as ScheduleIcon,
  Numbers as NumbersIcon,
  TextFields as TextIcon,
  List as ListIcon,
  ToggleOn as BooleanIcon,
  Api as ApiIcon,
  Input as InputIcon,
  Save as SaveIcon
} from '@mui/icons-material';

const BranchConditionBuilder = ({ 
  conditions = [], 
  onConditionsChange, 
  availableVariables = [],
  availableSteps = [],
  className = ""
}) => {
  const [localConditions, setLocalConditions] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const isInitialMount = useRef(true);

  // Initialize local conditions from props once
  useEffect(() => {
    if (isInitialMount.current) {
      if (Array.isArray(conditions) && conditions.length > 0) {
        setLocalConditions(conditions.map((condition, index) => ({
          id: condition.id || `condition_${index}`,
          ...condition
        })));
      } else {
        // Start with one empty condition
        setLocalConditions([createEmptyCondition()]);
      }
      isInitialMount.current = false;
    }
  }, []);

  // Notify parent of changes when localConditions change, but not on initial mount
  useEffect(() => {
    if (!isInitialMount.current) {
      const validConditions = localConditions.filter(isValidCondition);
      onConditionsChange?.(validConditions);
    }
  }, [localConditions]);

  function createEmptyCondition() {
    return {
      id: `condition_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type: 'variable_value', // Default type
      variable: '',
      operator: 'equals',
      value: '',
      comparisonValue: '',
      logicOperator: 'AND', // For compound logic
      nextStepId: '',
      description: ''
    };
  }

  function isValidCondition(condition) {
    if (!condition.variable || !condition.operator) return false;
    
    switch (condition.type) {
      case 'api_response':
      case 'user_input':
      case 'variable_value':
        return condition.value !== '' || ['exists', 'not_exists'].includes(condition.operator);
      case 'array_length':
      case 'numeric_comparison':
        return !isNaN(condition.value) && condition.value !== '';
      case 'boolean_logic':
        return ['is_true', 'is_false'].includes(condition.operator);
      case 'time_date':
        return condition.value !== '';
      case 'text_pattern':
        return condition.value !== '';
      default:
        return false;
    }
  }

  const conditionTypes = [
    {
      value: 'api_response',
      label: 'API Response Value',
      icon: <ApiIcon className="w-4 h-4" />,
      description: 'Check values from API responses'
    },
    {
      value: 'user_input',
      label: 'User Input Value',
      icon: <InputIcon className="w-4 h-4" />,
      description: 'Check user input content'
    },
    {
      value: 'variable_value',
      label: 'Saved Variable Value',
      icon: <SaveIcon className="w-4 h-4" />,
      description: 'Check saved variable values'
    },
    {
      value: 'array_length',
      label: 'Array/Object Length',
      icon: <ListIcon className="w-4 h-4" />,
      description: 'Check length of arrays or objects'
    },
    {
      value: 'boolean_logic',
      label: 'Boolean Logic',
      icon: <BooleanIcon className="w-4 h-4" />,
      description: 'Check true/false values'
    },
    {
      value: 'time_date',
      label: 'Time/Date Comparison',
      icon: <ScheduleIcon className="w-4 h-4" />,
      description: 'Compare dates and times'
    },
    {
      value: 'numeric_comparison',
      label: 'Numeric Comparison',
      icon: <NumbersIcon className="w-4 h-4" />,
      description: 'Compare numeric values'
    },
    {
      value: 'text_pattern',
      label: 'Text Pattern Match',
      icon: <TextIcon className="w-4 h-4" />,
      description: 'Pattern matching and regex'
    }
  ];

  const getOperatorsForType = (type) => {
    switch (type) {
      case 'api_response':
      case 'user_input':
      case 'variable_value':
        return [
          { value: 'equals', label: 'Equals' },
          { value: 'not_equals', label: 'Not Equals' },
          { value: 'contains', label: 'Contains' },
          { value: 'not_contains', label: 'Does Not Contain' },
          { value: 'exists', label: 'Exists' },
          { value: 'not_exists', label: 'Does Not Exist' },
          { value: 'greater_than', label: 'Greater Than' },
          { value: 'less_than', label: 'Less Than' }
        ];
      case 'array_length':
      case 'numeric_comparison':
        return [
          { value: 'equals', label: 'Equals' },
          { value: 'not_equals', label: 'Not Equals' },
          { value: 'greater_than', label: 'Greater Than' },
          { value: 'less_than', label: 'Less Than' },
          { value: 'greater_equal', label: 'Greater Than or Equal' },
          { value: 'less_equal', label: 'Less Than or Equal' }
        ];
      case 'boolean_logic':
        return [
          { value: 'is_true', label: 'Is True' },
          { value: 'is_false', label: 'Is False' }
        ];
      case 'time_date':
        return [
          { value: 'before', label: 'Before' },
          { value: 'after', label: 'After' },
          { value: 'equals', label: 'Equals' },
          { value: 'not_equals', label: 'Not Equals' }
        ];
      case 'text_pattern':
        return [
          { value: 'matches', label: 'Matches (Regex)' },
          { value: 'contains', label: 'Contains' },
          { value: 'starts_with', label: 'Starts With' },
          { value: 'ends_with', label: 'Ends With' },
          { value: 'not_contains', label: 'Does Not Contain' }
        ];
      default:
        return [];
    }
  };

  const getVariablesForType = (type) => {
    const baseVariables = [
      { value: 'user_message', label: 'User Message', type: 'global' },
      { value: 'user_name', label: 'User Name', type: 'global' },
      { value: 'user_email', label: 'User Email', type: 'global' },
      { value: 'session_id', label: 'Session ID', type: 'global' },
      { value: 'current_time', label: 'Current Time', type: 'global' },
      { value: 'current_date', label: 'Current Date', type: 'global' }
    ];

    // Add variables from previous steps
    const stepVariables = availableVariables.map(variable => ({
      value: variable.name,
      label: `${variable.name} (${variable.source})`,
      type: variable.type
    }));

    // Deduplicate variables by value (prioritize step variables over base variables)
    const combinedVariables = [...stepVariables, ...baseVariables];
    const uniqueVariables = [];
    const seenValues = new Set();
    
    for (const variable of combinedVariables) {
      if (!seenValues.has(variable.value)) {
        seenValues.add(variable.value);
        uniqueVariables.push(variable);
      }
    }

    return uniqueVariables;
  };

    const addCondition = () => {
    setLocalConditions(prevConditions => [...prevConditions, createEmptyCondition()]);
  };

  const removeCondition = (conditionId) => {
    setLocalConditions(prevConditions => prevConditions.filter(condition => condition.id !== conditionId));
  };

  const updateCondition = (conditionId, updates) => {
    setLocalConditions(prevConditions => 
      prevConditions.map(condition => 
        condition.id === conditionId 
          ? { 
              ...condition, 
              ...updates,
              // Reset operator when type changes
              ...(updates.type && updates.type !== condition.type ? { operator: getOperatorsForType(updates.type)[0]?.value || 'equals' } : {})
            }
          : condition
      )
    );
  };

  const generateConditionSummary = (condition) => {
    if (!isValidCondition(condition)) {
      return 'Incomplete condition';
    }

    const variableLabel = getVariablesForType(condition.type).find(v => v.value === condition.variable)?.label || condition.variable;
    const operatorLabel = getOperatorsForType(condition.type).find(o => o.value === condition.operator)?.label || condition.operator;
    
    let summary = `${variableLabel} ${operatorLabel}`;
    
    if (!['exists', 'not_exists', 'is_true', 'is_false'].includes(condition.operator)) {
      summary += ` "${condition.value}"`;
    }

    if (condition.nextStepId) {
      const nextStep = availableSteps.find(s => s.id === condition.nextStepId);
      summary += ` → ${nextStep?.name || 'Unknown Step'}`;
    }

    return summary;
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <LogicIcon className="w-5 h-5 mr-2 text-orange-600" />
          <h4 className="text-lg font-medium text-gray-900">Branch Conditions</h4>
        </div>
        <div className="flex items-center space-x-2">
          <button
            type="button"
            onClick={() => setShowPreview(!showPreview)}
            className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
          >
            <CodeIcon className="w-4 h-4 mr-1" />
            {showPreview ? 'Hide' : 'Show'} Preview
            {showPreview ? <ExpandLessIcon className="w-4 h-4 ml-1" /> : <ExpandMoreIcon className="w-4 h-4 ml-1" />}
          </button>
          <button
            type="button"
            onClick={addCondition}
            className="bg-orange-600 text-white px-3 py-1 rounded text-sm hover:bg-orange-700 flex items-center"
          >
            <AddIcon className="w-4 h-4 mr-1" />
            Add Condition
          </button>
        </div>
      </div>

      {/* Conditions Preview */}
      {showPreview && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h5 className="font-medium text-blue-900 mb-2">Condition Summary:</h5>
          <div className="space-y-1">
            {localConditions.map((condition, index) => (
              <div key={condition.id} className="text-sm text-blue-800">
                {index > 0 && (
                  <span className="font-medium text-blue-600 mr-2">
                    {condition.logicOperator || 'AND'}
                  </span>
                )}
                <span className="font-mono bg-blue-100 px-2 py-1 rounded">
                  {generateConditionSummary(condition)}
                </span>
              </div>
            ))}
            {localConditions.length === 0 && (
              <span className="text-blue-600">No conditions defined</span>
            )}
          </div>
        </div>
      )}

      {/* Conditions List */}
      <div className="space-y-4">
        {localConditions.map((condition, index) => (
          <div key={condition.id} className="bg-white border border-gray-200 rounded-lg p-4">
            {/* Condition Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <span className="inline-flex items-center justify-center w-6 h-6 bg-orange-100 text-orange-600 rounded-full text-sm font-medium mr-3">
                  {index + 1}
                </span>
                <h5 className="font-medium text-gray-900">
                  Condition {index + 1}
                  {condition.description && (
                    <span className="text-sm font-normal text-gray-500 ml-2">
                      - {condition.description}
                    </span>
                  )}
                </h5>
              </div>
              <div className="flex items-center space-x-2">
                {index > 0 && (
                  <select
                    value={condition.logicOperator || 'AND'}
                    onChange={(e) => updateCondition(condition.id, { logicOperator: e.target.value })}
                    className="text-sm border border-gray-300 rounded px-2 py-1 focus:ring-orange-500 focus:border-orange-500"
                  >
                    <option value="AND">AND</option>
                    <option value="OR">OR</option>
                  </select>
                )}
                {localConditions.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeCondition(condition.id)}
                    className="text-red-600 hover:text-red-800 p-1"
                    title="Remove condition"
                  >
                    <DeleteIcon className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            {/* Condition Configuration */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Condition Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Condition Type
                </label>
                <select
                  value={condition.type || 'variable_value'}
                  onChange={(e) => updateCondition(condition.id, { type: e.target.value, variable: '', value: '' })}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-orange-500 focus:border-orange-500 text-sm"
                >
                  {conditionTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {conditionTypes.find(t => t.value === condition.type)?.description}
                </p>
              </div>

              {/* Variable/Field Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Variable/Field
                </label>
                <select
                  value={condition.variable || ''}
                  onChange={(e) => updateCondition(condition.id, { variable: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-orange-500 focus:border-orange-500 text-sm"
                >
                  <option value="">Select variable...</option>
                  {getVariablesForType(condition.type).map((variable) => (
                    <option key={variable.value} value={variable.value}>
                      {variable.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Operator */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Operator
                </label>
                <select
                  value={condition.operator || 'equals'}
                  onChange={(e) => updateCondition(condition.id, { operator: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-orange-500 focus:border-orange-500 text-sm"
                >
                  {getOperatorsForType(condition.type).map((operator) => (
                    <option key={operator.value} value={operator.value}>
                      {operator.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Comparison Value */}
              {!['exists', 'not_exists', 'is_true', 'is_false'].includes(condition.operator) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {condition.type === 'time_date' ? 'Date/Time Value' : 
                     condition.type === 'numeric_comparison' || condition.type === 'array_length' ? 'Numeric Value' :
                     condition.type === 'text_pattern' && condition.operator === 'matches' ? 'Regular Expression' :
                     'Comparison Value'}
                  </label>
                  <input
                    type={
                      condition.type === 'numeric_comparison' || condition.type === 'array_length' ? 'number' :
                      condition.type === 'time_date' ? 'datetime-local' :
                      'text'
                    }
                    value={condition.value || ''}
                    onChange={(e) => updateCondition(condition.id, { value: e.target.value })}
                    placeholder={
                      condition.type === 'text_pattern' && condition.operator === 'matches' ? 'e.g., /(cancel|hapus|batalkan)/i' :
                      condition.type === 'time_date' ? 'Select date and time' :
                      condition.type === 'variable_value' ? 'Value or {{variable_name}}' :
                      'Enter comparison value'
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-orange-500 focus:border-orange-500 text-sm"
                  />
                  {condition.type === 'text_pattern' && condition.operator === 'matches' && (
                    <p className="text-xs text-gray-500 mt-1">
                      Use JavaScript regex format. Example: /(urgent|emergency)/i for case-insensitive matching
                    </p>
                  )}
                </div>
              )}

              {/* Next Step */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Next Step (if condition is met)
                </label>
                <select
                  value={condition.nextStepId || ''}
                  onChange={(e) => updateCondition(condition.id, { nextStepId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-orange-500 focus:border-orange-500 text-sm"
                >
                  <option value="">Continue to next step in order</option>
                  {availableSteps.map((step) => (
                    <option key={step.id} value={step.id}>
                      {step.name} ({step.type})
                    </option>
                  ))}
                </select>
              </div>

              {/* Description */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description (optional)
                </label>
                <input
                  type="text"
                  value={condition.description || ''}
                  onChange={(e) => updateCondition(condition.id, { description: e.target.value })}
                  placeholder="Brief description of this condition..."
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-orange-500 focus:border-orange-500 text-sm"
                />
              </div>
            </div>

            {/* Condition Status */}
            <div className="mt-4 pt-3 border-t border-gray-100">
              <div className="flex items-center">
                <div className={`w-3 h-3 rounded-full mr-2 ${
                  isValidCondition(condition) ? 'bg-green-500' : 'bg-red-500'
                }`}></div>
                <span className={`text-sm ${
                  isValidCondition(condition) ? 'text-green-700' : 'text-red-700'
                }`}>
                  {isValidCondition(condition) ? 'Valid condition' : 'Incomplete condition - please fill all required fields'}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Help Section */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <h5 className="font-medium text-yellow-900 mb-2">💡 Condition Tips</h5>
        <div className="text-sm text-yellow-800 space-y-1">
          <div>• <strong>AND logic:</strong> All conditions must be true</div>
          <div>• <strong>OR logic:</strong> Any condition can be true</div>
          <div>• <strong>Variable references:</strong> Use {`{{variable_name}}`} to reference other variables</div>
          <div>• <strong>Array length:</strong> Use field.length for arrays (e.g., doctors.length)</div>
          <div>• <strong>Regex patterns:</strong> Include flags like /i for case-insensitive matching</div>
          <div>• <strong>Date comparisons:</strong> Use ISO format or relative terms like "today", "tomorrow"</div>
        </div>
      </div>

      {/* Validation Summary */}
      {localConditions.length > 0 && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
          <div className="text-sm text-gray-700">
            <strong>Conditions Summary:</strong> {localConditions.filter(isValidCondition).length} of {localConditions.length} conditions are valid
          </div>
          {localConditions.filter(c => !isValidCondition(c)).length > 0 && (
            <div className="text-sm text-red-600 mt-1">
              Please complete all conditions before saving the flow.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BranchConditionBuilder; 